import {useCallback, useMemo, useState} from 'react';

export const StudioTab = {
	Prompt: 'prompt',
	AgentMd: 'agentMd',
	Deploy: 'deploy',
} as const;
export type StudioTab = (typeof StudioTab)[keyof typeof StudioTab];

export interface StudioAgentEntry {
	agentId: string;
	name: string;
	categoryId: string;
	title: string;
	description: string;
}

export interface StudioCategoryEntry {
	categoryId: string;
	name: string;
	isUncategorised: boolean;
}

export interface CatalogueGroup {
	category: StudioCategoryEntry;
	agents: StudioAgentEntry[];
}

export interface StudioCatalogue {
	groups: CatalogueGroup[];
}

export function buildStudioCatalogue(categories: StudioCategoryEntry[], agents: StudioAgentEntry[]): StudioCatalogue {
	const knownCategoryIds = new Set(categories.map(category => category.categoryId));
	const groups = categories.map(category => ({
		category,
		agents: agents.filter(
			agent =>
				agent.categoryId === category.categoryId ||
				(category.isUncategorised && !knownCategoryIds.has(agent.categoryId))
		),
	}));
	return {groups};
}

export interface UseNovaStudioShellOptions {
	categories: StudioCategoryEntry[];
	agents: StudioAgentEntry[];
	initialSelectedAgentId?: string | null;
	initialTab?: StudioTab;
}

export interface NovaStudioShellState {
	catalogue: StudioCatalogue;
	selectedAgentId: string | null;
	selectedAgent: StudioAgentEntry | null;
	activeTab: StudioTab;
	selectAgent: (agentId: string | null) => void;
	setActiveTab: (tab: StudioTab) => void;
}

export function useNovaStudioShell(options: UseNovaStudioShellOptions): NovaStudioShellState {
	const {categories, agents} = options;
	const [selectedAgentId, setSelectedAgentId] = useState<string | null>(options.initialSelectedAgentId ?? null);
	const [activeTab, setActiveTab] = useState<StudioTab>(options.initialTab ?? StudioTab.Prompt);

	const catalogue = useMemo(() => buildStudioCatalogue(categories, agents), [categories, agents]);
	const selectedAgent = useMemo(
		() => agents.find(agent => agent.agentId === selectedAgentId) ?? null,
		[agents, selectedAgentId]
	);

	const selectAgent = useCallback(
		(agentId: string | null) => {
			// Opening a different agent lands on its Prompt tab; switching tabs within
			// the same agent stays put (tabs are worked independently).
			if (agentId !== selectedAgentId) {
				setActiveTab(StudioTab.Prompt);
			}
			setSelectedAgentId(agentId);
		},
		[selectedAgentId]
	);

	return {
		catalogue,
		selectedAgentId,
		selectedAgent,
		activeTab,
		selectAgent,
		setActiveTab,
	};
}
